import React, { createContext, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { 
  getOwnerKeyFromContext, 
  selectClientsByOwnerKey, 
  dispatchClientAction,
  fetchClients,
  createClient,
  updateClient,
  deleteClient,
  selectClientById
} from 'app/store/clientsSlice';

const ClientAppContext = createContext();

export const ClientAppProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { groupId } = useParams(); // Organization channel ID from route
  const user = useSelector(state => state.user);
  
  const ownerKey = useMemo(() => 
    getOwnerKeyFromContext(user, groupId), 
    [user, groupId]
  );
  
  const contextValue = useMemo(() => ({
    ownerKey,
    isOrganizationContext: !!groupId,
    
    // Unified client operations
    getClient: (clientId) => {
      return useSelector(selectClientById(clientId));
    },
    
    fetchClients: () => {
      return dispatchClientAction(dispatch, fetchClients, ownerKey, {});
    },
    
    createClient: (clientData) => {
      return dispatchClientAction(dispatch, createClient, ownerKey, clientData);
    },
    
    updateClient: (clientData) => {
      return dispatchClientAction(dispatch, updateClient, ownerKey, clientData);
    },
    
    deleteClient: (clientId) => {
      return dispatchClientAction(dispatch, deleteClient, ownerKey, { clientId });
    }
  }), [dispatch, ownerKey, groupId]);

  return (
    <ClientAppContext.Provider value={contextValue}>
      {children}
    </ClientAppContext.Provider>
  );
};

export const useClientApp = () => {
  const context = useContext(ClientAppContext);
  if (!context) {
    throw new Error('useClientApp must be used within a ClientAppProvider');
  }
  return context;
};

export default ClientAppContext;